import {
  trackToolExperience,
  deviceLayout,
} from "../../features/toolExperience/telemetry";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// import { useQuery } from '@tanstack/react-query'; // Not used
import {
  ToolPage,
  ToolHeader,
  CollectionSummary,
} from "../../components/ToolPage";
import MonitoringProjectList from "./MonitoringProjectList";
import { useMonitoringAttention } from "./useMonitoringAttention";
import { MonitoringAttention } from "./MonitoringAttention";
import RecoveryNotice from "../../components/RecoveryNotice/RecoveryNotice";
import { Button } from "../../components/ui/Button/Button";
import { userMessage } from "../../utils/userMessage";
import ProjectWizard from "./ProjectWizard";
import { ConfirmModal } from "../../components/Modal/Modal";
import { useMonitoringProjects } from "../../hooks/useMonitoringProjects";
import { useToast } from "../../hooks/useToast";
import {
  CRAWL_ALREADY_RUNNING_CODE,
  triggerCrawl,
  type MonitoringApiError,
  type MonitoringProject,
} from "../../api/monitoring";

import { logger } from "../../utils/logger";
import { monitoringIntentAPI } from "../../api/monitoringIntent";
import {
  clearMonitoringIntent,
  getMonitoringIntent,
  setMonitoringIntent,
  type MonitoringIntent,
} from "../../utils/monitoringIntent";

async function finishMonitoringHandoff(
  id: string,
  action: "complete" | "dismiss",
): Promise<void> {
  for (const delay of [0, 1000, 3000]) {
    if (delay)
      await new Promise((resolve) => window.setTimeout(resolve, delay));
    try {
      await monitoringIntentAPI[action](id);
      const local = getMonitoringIntent();
      if (local?.id === id) clearMonitoringIntent();
      return;
    } catch (error) {
      logger.warn("Could not finish monitoring handoff; will retry:", error);
    }
  }
}

export default function MonitoringDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [showWizard, setShowWizard] = useState(false);
  const [handoffIntent, setHandoffIntent] = useState<MonitoringIntent | null>(
    () => getMonitoringIntent(),
  );
  const isCreatingProjectRef = useRef(false);

  useEffect(() => {
    if (
      new URLSearchParams(location.search).get("create") === "1" &&
      (!handoffIntent || handoffIntent.state === "claimed")
    ) {
      setShowWizard(true);
      const params = new URLSearchParams(location.search);
      params.delete("create");
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate, handoffIntent]);

  const dismissHandoff = () => {
    if (handoffIntent) {
      setMonitoringIntent({ ...handoffIntent, state: "dismissed" });
      void finishMonitoringHandoff(handoffIntent.id, "dismiss");
      setHandoffIntent(null);
    }
    setShowWizard(false);
    navigate("/monitoring", { replace: true });
  };
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    project: MonitoringProject | null;
  }>({ isOpen: false, project: null });

  const {
    projects,
    isLoading,
    error: projectsError,
    createProject,
    deleteProject,
    pauseProject,
    resumeProject,
    refreshProjects,
  } = useMonitoringProjects(1, "all");

  const handleCreateProject = () => {
    setShowWizard(true);
  };

  const handleWizardComplete = async (projectData: {
    name: string;
    url?: string;
    sitemap_urls?: string[];
    webhook_url?: string;
    custom_headers?: Record<string, string>;
    scope_type?: import("../../api/types").ScopeType;
    specific_urls?: string[];
    segment_filters?: import("../../api/types").SegmentFilter[];
    max_urls?: number;
    crawl_frequency?: "daily" | "weekly";
    onboarding_intent_id?: string;
  }) => {
    if (isCreatingProjectRef.current) return;
    isCreatingProjectRef.current = true;
    try {
      const newProject = await createProject({
        ...projectData,
        ...(handoffIntent ? { onboarding_intent_id: handoffIntent.id } : {}),
      });
      trackToolExperience("task_submitted", {
        toolId: "monitoring",
        source: "page",
        layout: deviceLayout(),
      });
      if (handoffIntent) {
        setMonitoringIntent({
          ...handoffIntent,
          state: "project_created",
          project_id: newProject.id,
        });
        try {
          await monitoringIntentAPI.projectCreated(
            handoffIntent.id,
            newProject.id,
          );
          setMonitoringIntent({
            ...handoffIntent,
            state: "completed",
            project_id: newProject.id,
          });
          void finishMonitoringHandoff(handoffIntent.id, "complete");
        } catch (error) {
          logger.warn(
            "Monitoring project created; handoff completion will retry on next sign-in:",
            error,
          );
        }
        setHandoffIntent(null);
      }
      setShowWizard(false);
      navigate(`/monitoring/${newProject.id}`, { replace: true });
    } catch (error) {
      logger.error("Failed to create project:", error);
      throw error;
    } finally {
      isCreatingProjectRef.current = false;
    }
  };

  const handleProjectDelete = (project: MonitoringProject) => {
    setDeleteModal({ isOpen: true, project });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.project) return;

    try {
      await deleteProject(deleteModal.project.id);
      setDeleteModal({ isOpen: false, project: null });
    } catch (error) {
      logger.error("Failed to delete project:", error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, project: null });
  };

  const handleTriggerCrawl = async (projectId: string, force = false) => {
    try {
      await triggerCrawl(projectId, undefined, force);
      // Refresh the projects list to get updated status
      refreshProjects();
    } catch (error: unknown) {
      const apiError = error as MonitoringApiError;
      logger.error("Failed to trigger crawl:", apiError);

      if (apiError.code === CRAWL_ALREADY_RUNNING_CODE) {
        const details = apiError.details as { message?: string } | undefined;
        toast.warning(
          "Crawl Already Running",
          details?.message ||
            apiError.message ||
            "A crawl is already running. Force restart only if it appears stuck.",
          {
            duration: 0,
            action: {
              label: "Force Crawl",
              onClick: () => {
                void handleTriggerCrawl(projectId, true);
              },
            },
          },
        );
        return;
      }

      // Handle insufficient credits error
      if (apiError.code === "INSUFFICIENT_CREDITS") {
        const details = apiError.details as
          | {
              message?: string;
              required_credits?: number;
              upgrade_url?: string;
            }
          | undefined;
        toast.error(
          "Insufficient Credits",
          details?.message ||
            `Estimated ${details?.required_credits || 0} credits needed to crawl this project.`,
          {
            duration: 0, // Don't auto-dismiss
            action: details?.upgrade_url
              ? {
                  label: "Upgrade Plan",
                  onClick: () => {
                    navigate("/admin/billing/upgrade");
                  },
                }
              : undefined,
          },
        );
      } else {
        // Generic error notification
        toast.error(
          "Crawl Failed",
          apiError.message || "Failed to start crawl. Please try again.",
          {
            duration: 7000,
          },
        );
      }
    }
  };

  const handlePauseProject = async (projectId: string) => {
    try {
      await pauseProject(projectId);
    } catch (error) {
      logger.error("Failed to pause project:", error);
      toast.error(
        "Pause Failed",
        "Could not pause the project. Please try again.",
        { duration: 5000 },
      );
    }
  };

  const handleResumeProject = async (projectId: string) => {
    try {
      await resumeProject(projectId);
    } catch (error) {
      logger.error("Failed to resume project:", error);
      toast.error(
        "Resume Failed",
        "Could not resume the project. Please try again.",
        { duration: 5000 },
      );
    }
  };

  const attention = useMonitoringAttention();
  const queryClient = useQueryClient();

  return (
    <ToolPage toolId="monitoring">
      {/* Create flow is a modal overlaying the dashboard, matching every other
          "create X" flow in the app — it no longer replaces the whole page. */}
      {showWizard && (
        <ProjectWizard
          initialIntent={handoffIntent}
          onComplete={handleWizardComplete}
          onCancel={() => {
            if (handoffIntent) dismissHandoff();
            else setShowWizard(false);
          }}
        />
      )}
      <ToolHeader
        title="Monitoring"
        primaryAction={{
          label: "Monitor website",
          onSelect: handleCreateProject,
        }}
      />
      <CollectionSummary
        scope="complete"
        items={[
          { label: "websites", value: projectsError ? null : projects.length },
        ]}
      />
      <MonitoringAttention
        changes={attention.changes.data || []}
        onRefresh={async () => {
          await attention.changes.refetch();
          await queryClient.invalidateQueries({
            queryKey: ["dashboard", "work-source", "monitoring-changes"],
          });
          refreshProjects();
        }}
      />
      {[attention.active, attention.changes].some(
        (q) =>
          q.isError || (q.isFetching && q.errorUpdatedAt > q.dataUpdatedAt),
      ) && (
        <RecoveryNotice
          onRetry={() =>
            Promise.all([
              attention.active.refetch(),
              attention.changes.refetch(),
            ])
          }
        >
          Monitoring updates are temporarily unavailable.
        </RecoveryNotice>
      )}
      {projectsError && (
        <Button
          variant="secondary"
          onClick={() => {
            void refreshProjects();
          }}
        >
          Retry loading projects
        </Button>
      )}
      <MonitoringProjectList
        projects={projects}
        loading={isLoading}
        error={
          projectsError
            ? userMessage(
                projectsError,
                "Could not load your websites. Please try again.",
              )
            : null
        }
        activeIds={(attention.active.data || []).map((c) => c.project_id)}
        activeUnknown={attention.active.isError || attention.active.isLoading}
        onDelete={handleProjectDelete}
        onCheck={async (id) => {
          await handleTriggerCrawl(id);
          await attention.active.refetch();
        }}
        onPause={handlePauseProject}
        onResume={handleResumeProject}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Monitoring Project"
        message={`Are you sure you want to delete "${deleteModal.project?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </ToolPage>
  );
}
